import React, { useState, useEffect, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import {
  makeStyles,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Checkbox,
  Chip,
  FormControlLabel,
} from "@material-ui/core";
import { green } from "@material-ui/core/colors";
import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  root: {
    flexWrap: "wrap",
  },
  textField: {
    marginRight: theme.spacing(1),
    width: "100%",
  },

  btnWrapper: {
    position: "relative",
  },

  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  textQuickAnswerContainer: {
    width: "100%",
  },
}));

const QuickAnswerSchema = Yup.object().shape({
  shortcut: Yup.string()
    .min(2, "Too Short!")
    .max(15, "Too Long!")
    .required("Required"),
  message: Yup.string()
    .min(8, "Too Short!")
    .max(30000, "Too Long!")
    .required("Required"),
});

// variáveis aceitas nas respostas rápidas (preenchidas com o card da conversa)
const VARIAVEIS = [
  ["nome", "Nome do contato no WhatsApp"], ["os", "Número da O.S"], ["conta", "Conta do cliente"],
  ["filial", "Filial / nome do cliente"], ["tecnico", "Nome do técnico"], ["qra", "QRA do técnico"],
  ["tipo", "Tipo de serviço"], ["sistema", "Sistema"], ["protocolo", "Gera o protocolo do card e insere a resposta pronta"],
];

const QuickAnswersModal = ({
  open,
  onClose,
  quickAnswerId,
  initialValues,
  onSave,
}) => {
  const classes = useStyles();
  const isMounted = useRef(true);

  const initialState = {
    shortcut: "",
    message: "",
    queueIds: [],
    anexos: [],
    link: "",
  };
  const [enviando, setEnviando] = useState(false);
  const anexar = async (files, values, setFieldValue) => {
    const lista = Array.from(files || []);
    if (!lista.length) return;
    const grande = lista.find((f) => f.size > 20 * 1024 * 1024);
    if (grande) return toast.error(`"${grande.name}" passa de 20 MB`);
    const fd = new FormData();
    lista.forEach((f) => fd.append("arquivos", f));
    setEnviando(true);
    try {
      const { data } = await api.post("/quickAnswers/anexos", fd);
      setFieldValue("anexos", [...(values.anexos || []), ...data]);
    } catch (err) {
      toastError(err);
    }
    setEnviando(false);
  };

  const [quickAnswer, setQuickAnswer] = useState(initialState);
  const [filas, setFilas] = useState([]);
  useEffect(() => {
    api.get("/queue").then(({ data }) => setFilas(data)).catch(() => {});
  }, []);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    const fetchQuickAnswer = async () => {
      if (initialValues) {
        setQuickAnswer((prevState) => {
          return { ...prevState, ...initialValues };
        });
      }

      if (!quickAnswerId) return;

      try {
        const { data } = await api.get(`/quickAnswers/${quickAnswerId}`);
        if (isMounted.current) {
          setQuickAnswer({ ...data, queueIds: data.queueIds || [], anexos: data.anexos || [], link: data.link || "" });
        }
      } catch (err) {
        toastError(err);
      }
    };

    fetchQuickAnswer();
  }, [quickAnswerId, open, initialValues]);

  const handleClose = () => {
    onClose();
    setQuickAnswer(initialState);
  };

  const handleSaveQuickAnswer = async (values) => {
    try {
      if (quickAnswerId) {
        await api.put(`/quickAnswers/${quickAnswerId}`, values);
        handleClose();
      } else {
        const { data } = await api.post("/quickAnswers", values);
        if (onSave) {
          onSave(data);
        }
        handleClose();
      }
      toast.success(i18n.t("quickAnswersModal.success"));
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        scroll="paper"
      >
        <DialogTitle id="form-dialog-title">
          {quickAnswerId
            ? `${i18n.t("quickAnswersModal.title.edit")}`
            : `${i18n.t("quickAnswersModal.title.add")}`}
        </DialogTitle>
        <Formik
          initialValues={quickAnswer}
          enableReinitialize={true}
          validationSchema={QuickAnswerSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveQuickAnswer(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ values, errors, touched, isSubmitting, setFieldValue }) => (
            <Form>
              <DialogContent dividers>
                <div className={classes.textQuickAnswerContainer}>
                  <Field
                    as={TextField}
                    label={i18n.t("quickAnswersModal.form.shortcut")}
                    name="shortcut"
                    autoFocus
                    error={touched.shortcut && Boolean(errors.shortcut)}
                    helperText={touched.shortcut && errors.shortcut}
                    variant="outlined"
                    margin="dense"
                    className={classes.textField}
                    fullWidth
                  />
                </div>
                <div className={classes.textQuickAnswerContainer}>
                  <Field
                    as={TextField}
                    label={i18n.t("quickAnswersModal.form.message")}
                    name="message"
                    error={touched.message && Boolean(errors.message)}
                    helperText={touched.message && errors.message}
                    variant="outlined"
                    margin="dense"
                    className={classes.textField}
                    multiline
                    rows={5}
                    fullWidth
                  />
                </div>
                <div className={classes.textQuickAnswerContainer} style={{ display: "block" }}>
                  <div style={{ fontWeight: 500, marginTop: 4 }}>Anexos</div>
                  <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 6 }}>
                    PDF, imagem ou vídeo (até 20 MB cada). Vão junto com a mensagem quando a resposta for usada.
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
                    {(values.anexos || []).map((a, i) => (
                      <Chip key={a.arquivo} size="small"
                        label={`${/^image\//.test(a.tipo) ? "🖼" : /^video\//.test(a.tipo) ? "🎥" : "📄"} ${a.nome}`}
                        onDelete={() => setFieldValue("anexos", values.anexos.filter((_, j) => j !== i))} />
                    ))}
                    <Button size="small" variant="outlined" color="primary" component="label" disabled={enviando}>
                      {enviando ? "Enviando…" : "📎 Anexar arquivo"}
                      <input type="file" hidden multiple accept="image/*,application/pdf,video/*,.doc,.docx,.xls,.xlsx"
                        onChange={(e) => { anexar(e.target.files, values, setFieldValue); e.target.value = ""; }} />
                    </Button>
                  </div>
                  <Field as={TextField} name="link" label="Link (opcional)" placeholder="https://…"
                    variant="outlined" margin="dense" fullWidth
                    helperText="Vai no fim da mensagem." />
                </div>
                <div className={classes.textQuickAnswerContainer} style={{ display: "block" }}>
                  <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 4 }}>
                    Variáveis (clique para inserir) — na conversa viram os dados do card:
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {VARIAVEIS.map(([nome, ajuda]) => (
                      <Chip key={nome} size="small" clickable variant="outlined" color="primary"
                        label={`{{${nome}}}`} title={ajuda}
                        onClick={() => setFieldValue("message", `${values.message || ""}{{${nome}}}`)} />
                    ))}
                  </div>
                </div>
                {(
                  <div className={classes.textQuickAnswerContainer} style={{ display: "block" }}>
                    <div style={{ fontWeight: 500, marginTop: 8 }}>Mostrar só nas filas</div>
                    {filas.map((q) => (
                      <FormControlLabel
                        key={q.id}
                        label={q.name}
                        control={
                          <Checkbox
                            color="primary"
                            size="small"
                            checked={(values.queueIds || []).includes(q.id)}
                            onChange={(e) =>
                              setFieldValue(
                                "queueIds",
                                e.target.checked
                                  ? [...(values.queueIds || []), q.id]
                                  : (values.queueIds || []).filter((x) => x !== q.id)
                              )
                            }
                          />
                        }
                      />
                    ))}
                    <div style={{ fontSize: 12, opacity: 0.7 }}>
                      Sem fila marcada, a resposta aparece em todas as filas.
                    </div>
                  </div>
                )}
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleClose}
                  color="secondary"
                  disabled={isSubmitting}
                  variant="outlined"
                >
                  {i18n.t("quickAnswersModal.buttons.cancel")}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting}
                  variant="contained"
                  className={classes.btnWrapper}
                >
                  {quickAnswerId
                    ? `${i18n.t("quickAnswersModal.buttons.okEdit")}`
                    : `${i18n.t("quickAnswersModal.buttons.okAdd")}`}
                  {isSubmitting && (
                    <CircularProgress
                      size={24}
                      className={classes.buttonProgress}
                    />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default QuickAnswersModal;
